/**
 * Shady Hero Web Component
 * High performance video hero with IntersectionObserver lifecycle management
 */
if (!customElements.get('shady-hero')) {
  class ShadyHero extends HTMLElement {
    constructor() {
      super();
      this.video = this.querySelector('video');
      this.poster = this.querySelector('.shady-hero__poster');
      this.toggleBtn = this.querySelector('.shady-hero__play-pause-btn');
      this.iframe = this.querySelector('iframe');
      this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.observer = null;
      this.userPaused = false;
    }

    connectedCallback() {
      this.initVideo();
      this.initToggle();
      this.initObserver();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    initVideo() {
      if (!this.video) return;

      // Handle poster fade once video data is playing
      this.video.addEventListener('playing', () => {
        if (this.poster) {
          this.poster.classList.add('is-hidden');
        }
      }, { once: true });

      // Respect user's reduced motion system preferences
      if (this.isReducedMotion) {
        this.video.pause();
        this.updateToggleState(true);
      }
    }

    initToggle() {
      if (!this.toggleBtn) return;

      this.toggleBtn.addEventListener('click', () => {
        if (this.video) {
          if (this.video.paused) {
            this.video.play();
            this.userPaused = false;
            this.updateToggleState(false);
          } else {
            this.video.pause();
            this.userPaused = true;
            this.updateToggleState(true);
          }
        } else if (this.iframe) {
          // Post message for YouTube / Vimeo iframes
          const isPaused = this.toggleBtn.classList.contains('is-paused');
          const command = isPaused ? 'play' : 'pause';
          this.toggleIframe(command);
          this.updateToggleState(!isPaused);
        }
      });
    }

    toggleIframe(command) {
      if (!this.iframe || !this.iframe.contentWindow) return;
      if (this.iframe.src.includes('youtube.com')) {
        const func = command === 'play' ? 'playVideo' : 'pauseVideo';
        this.iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func }), '*');
      } else if (this.iframe.src.includes('vimeo.com')) {
        this.iframe.contentWindow.postMessage(JSON.stringify({ method: command }), '*');
      }
    }

    updateToggleState(isPaused) {
      if (!this.toggleBtn) return;
      this.toggleBtn.classList.toggle('is-paused', isPaused);
      this.toggleBtn.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
      this.toggleBtn.setAttribute('aria-label', isPaused ? 'Play background video' : 'Pause background video');
    }

    initObserver() {
      if (!('IntersectionObserver' in window)) {
        if (this.video && !this.isReducedMotion) {
          this.video.play().catch(() => {});
        }
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!this.video) return;

          if (entry.isIntersecting) {
            // In viewport: resume playback unless user manually paused
            if (!this.userPaused && !this.isReducedMotion && this.video.paused) {
              const playPromise = this.video.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {});
              }
              this.updateToggleState(false);
            }
          } else {
            // Out of viewport: pause video to free up CPU / GPU resources
            if (!this.video.paused) {
              this.video.pause();
            }
          }
        });
      }, {
        rootMargin: '100px 0px',
        threshold: 0.1
      });

      this.observer.observe(this);
    }
  }

  customElements.define('shady-hero', ShadyHero);
}
